import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useNotify } from "@/hooks/useNotify";
import { ExternalLink, Clipboard } from "lucide-react";

interface InvitationLinkHandlerProps {
  className?: string;
  variant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive";
}

const InvitationLinkHandler = ({ className, variant = "default" }: InvitationLinkHandlerProps) => {
  const [invitationUrl, setInvitationUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const notify = useNotify();

  const extractInvitationParams = (url: string) => {
    try {
      // Handle both full URLs and just the path
      const urlObj = url.startsWith('http') ? new URL(url) : new URL(url, window.location.origin);
      const params = new URLSearchParams(urlObj.search);
      
      // Check if it's a join URL
      if (!urlObj.pathname.includes('/join')) {
        return null;
      }

      const invitation = params.get('invitation');
      const token = params.get('token');
      
      if (invitation && token) {
        return { invitation, token };
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invitationUrl.trim()) {
      notify.error("Please paste your invitation link.");
      return;
    }

    setIsLoading(true);

    try {
      const params = extractInvitationParams(invitationUrl.trim());
      
      if (!params) {
        notify.error("The link you provided is not a valid invitation link. Please check and try again.");
        return;
      }

      // Navigate to join page with extracted parameters
      navigate(`/join?invitation=${params.invitation}&token=${params.token}`);
      setIsOpen(false);
      setInvitationUrl("");
      
    } catch (error) {
      notify.error("Failed to process the invitation link. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setInvitationUrl(text);
    } catch (error) {
      notify.error("Unable to read from clipboard. Please paste the link manually.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className={className} variant={variant}>
          <ExternalLink className="h-4 w-4 mr-2" />
          Join with invitation link
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Join with Invitation Link</DialogTitle>
          <DialogDescription>
            Paste the invitation link you received via email or other communication.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="invitation-url" className="text-sm font-medium">
              Invitation Link
            </label>
            <div className="flex gap-2">
              <Input
                id="invitation-url"
                placeholder="https://yourapp.com/join?invitation=...&token=..."
                value={invitationUrl}
                onChange={(e) => setInvitationUrl(e.target.value)}
                className="flex-1"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handlePasteFromClipboard}
                className="px-3"
              >
                <Clipboard className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              The link should contain both an invitation ID and token.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsOpen(false);
                setInvitationUrl("");
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Processing..." : "Join Company"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InvitationLinkHandler;